import type { OrderFormValues } from '@/components/ls-order-form/LsOrderForm';
import type { CartItem } from '@/features/cart/cart.store';
import { buildOrderPayload } from '@/features/order/submit-order';
import { initDataHeader } from '@mini/platform/telegram';

/**
 * Оплата заказа.
 *
 * Ручки уже есть на сервере (`services/widget-api/app/api/checkout.py`),
 * здесь только вызовы. Порядок важен и меняться не должен:
 *
 * 1. заказ создаётся **до** оплаты. Если карта не пройдёт, у нас останутся
 *    и состав, и контакты, и макет — потерять собранную вещь из-за отказа
 *    банка худшее, что тут может случиться;
 * 2. страница провайдера открывается отдельным запросом, потому что
 *    человек может вернуться и заплатить позже по тому же заказу;
 * 3. правда об оплате приходит только обратным вызовом провайдера
 *    на сервер. Возвращение человека в приложение не доказывает ничего:
 *    вернуться можно и не заплатив.
 */

const BASE = import.meta.env.VITE_API_URL ?? '';

export class CheckoutError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'CheckoutError';
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}/api/v1${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      // Подпись телеграма. Без неё сервер не отдаёт заказ: ссылку легко
      // переслать, а внутри адрес и телефон.
      ...initDataHeader(),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new CheckoutError(detail || response.statusText, response.status);
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

export interface Checkout {
  orderNumber: number;
  totalRub: number;
  /** Ключ заказа: по нему открывается оплата и читается состояние. */
  token: string;
}

interface CheckoutResponse {
  order_number: number;
  total_rub: number;
  miniapp_url: string;
}

export interface OrderState {
  orderNumber: number;
  totalRub: number;
  paymentStatus: string;
}

/**
 * Создать заказ и получить его ключ.
 *
 * Ключ идемпотентности собирается из состава: повторное нажатие
 * на «Оформить» при моргнувшей сети не должно создавать второй заказ.
 */
export async function startCheckout(
  partnerSlug: string,
  items: CartItem[],
  values: OrderFormValues,
): Promise<Checkout> {
  const payload = buildOrderPayload({ partnerSlug, items, values });

  const data = await call<CheckoutResponse>(`/partners/${partnerSlug}/checkout`, {
    method: 'POST',
    headers: { 'Idempotency-Key': idempotencyKey(items, values) },
    body: JSON.stringify(payload),
  });

  return {
    orderNumber: data.order_number,
    totalRub: data.total_rub,
    token: tokenFrom(data.miniapp_url),
  };
}

/** Открыть оплату: сервер обращается к провайдеру и отдаёт адрес страницы. */
export async function openPayment(token: string): Promise<string> {
  const data = await call<{ pay_url: string }>(`/checkout/${token}/session`, { method: 'POST' });
  return data.pay_url;
}

/** Перечитать заказ — например, когда человек вернулся со страницы оплаты. */
export async function readOrder(token: string): Promise<OrderState> {
  const data = await call<{ order_number: number; total_rub: number; payment_status: string }>(
    `/checkout/${token}`,
  );

  return {
    orderNumber: data.order_number,
    totalRub: data.total_rub,
    paymentStatus: data.payment_status,
  };
}

/**
 * Ключ заказа из ссылки, которую вернул сервер.
 *
 * NOTE: сервер отдаёт готовую ссылку в мини-приложение — она нужна тем,
 * кто уводит человека снаружи. Мы уже внутри, и нам нужен сам ключ.
 * Когда у ручки появится поле `token`, эта функция уйдёт.
 */
function tokenFrom(miniappUrl: string): string {
  try {
    return new URL(miniappUrl).searchParams.get('startapp') ?? '';
  } catch {
    return '';
  }
}

/**
 * Ключ идемпотентности: состав заказа и телефон.
 *
 * Не случайное число: при повторном нажатии случайное даст новый ключ
 * и новый заказ, а смысл ключа ровно в обратном.
 */
function idempotencyKey(items: CartItem[], values: OrderFormValues): string {
  const source = items
    .map((item) => `${item.productId}:${item.color}:${item.size}:${item.qty}`)
    .join('|');

  return hash(`${source}|${values.phone}`);
}

/** Короткая устойчивая свёртка строки: серверу нужны 8–64 знака. */
function hash(source: string): string {
  let value = 5381;

  for (let index = 0; index < source.length; index += 1) {
    value = ((value << 5) + value + source.charCodeAt(index)) | 0;
  }

  return `ls-${Math.abs(value).toString(36)}-${source.length.toString(36)}`.slice(0, 64);
}
